import React, { useState, useEffect, useContext } from 'react';
import { ThemeContext } from '../App';
import { Capacitor } from '@capacitor/core';
import { useNetworkStatus } from '../hooks/useNetworkStatus';
import { addPhotoToQueue, getQueue, removeFromQueue, QUEUE_UPDATED_EVENT } from '../services/offlineQueue';
// Importação condicional do Capacitor Camera
let Camera;
if (typeof window !== 'undefined' && window.Capacitor) {
  try {
    ({ Camera } = require('@capacitor/camera'));
  } catch (e) {}
}
import { supabase } from '../supabaseClient';

import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
pdfMake.vfs = pdfFonts.vfs;

// Importação condicional do Capacitor Filesystem
let Filesystem, Directory;
if (typeof window !== 'undefined' && window.Capacitor) {
  try {
    // eslint-disable-next-line
    ({ Filesystem, Directory } = require('@capacitor/filesystem'));
  } catch (e) {
    // Ignora se não estiver disponível
  }
}

// Importação condicional do File Opener
let FileOpener;
if (typeof window !== 'undefined' && window.Capacitor) {
  try {
    ({ FileOpener } = require('@capacitor-community/file-opener'));
  } catch (e) {
    // Ignora se não estiver disponível
  }
}
import { DadosMeteorologicosService } from '../services/DadosMeteorologicosService';
import { calcularFasesFenologicas, obterFasePorData } from '../utils/fenologia';
import { calcularPeriodoMeteorologicoFotos } from './albumFotosMeteorologia';
import { fotoPertenceAoPlantio, plantioScopeId } from './albumFotosPlantio';
import { usePlanLimits } from '../hooks/usePlanLimits';
import UpgradeModal from '../components/UpgradeModal';
import './AlbumFotos.css';

function isoToLocalDate(isoString) {
  if (!isoString) return '';
  // String pura YYYY-MM-DD: retorna como está (sem conversão UTC)
  if (/^\d{4}-\d{2}-\d{2}$/.test(isoString)) return isoString;
  // Timestamp completo: usa getters locais para evitar problema de fuso
  const date = new Date(isoString);
  if (isNaN(date)) return '';
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// Retorna a data LOCAL atual como timestamp meio-dia UTC (ex: "2026-07-02T12:00:00Z")
// Evita que new Date().toISOString() registre o dia errado quando o horário local
// está próximo da meia-noite UTC (ex: 22h em UTC-3 = 01h UTC do dia seguinte).
function localNoonUTC() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}T12:00:00Z`;
}

// Comprime imagem via Canvas: redimensiona para max 1920px e exporta JPEG quality 0.80
function compressImage(file, maxSize = 1920, quality = 0.80) {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width >= height) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob || file), 'image/jpeg', quality);
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
    img.src = url;
  });
}

// Converte Blob/File para base64 puro (sem prefixo data:...)
function blobToBase64(blob) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result.split(',')[1]);
    reader.readAsDataURL(blob);
  });
}

// Comprime imagem a partir de base64 (Capacitor)
function compressBase64Image(base64, format, maxSize = 1920, quality = 0.80) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      let { width, height } = img;
      if (width > maxSize || height > maxSize) {
        if (width >= height) {
          height = Math.round((height * maxSize) / width);
          width = maxSize;
        } else {
          width = Math.round((width * maxSize) / height);
          height = maxSize;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      canvas.getContext('2d').drawImage(img, 0, 0, width, height);
      canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
    };
    img.onerror = () => resolve(null);
    img.src = `data:image/${format};base64,${base64}`;
  });
}

function AlbumFotos({ talhao, user, permission = 'owner' }) {
  const { darkMode } = useContext(ThemeContext);
  // Detecta se está no desktop
  const isDesktop = !Capacitor.isNativePlatform() && window.navigator.userAgent.includes('Electron');
  const isOnline = useNetworkStatus();
  const [dragOver, setDragOver] = useState(false);
  
  // Permissão: 'owner' e 'editor' podem editar, 'viewer' só visualiza
  const podeEditar = permission === 'owner' || permission === 'editor';
  
  // Hook de verificação de plano
  const { podeUsar, loading: planLoading } = usePlanLimits(user?.id);
  // Evita mostrar cadeado de bloqueio antes do plano terminar de carregar
  const podeUsarPdf = planLoading || podeUsar('relatorios_pdf');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  
  // Estado para armazenar dados meteorológicos e fenologia de cada foto
  const [fotos, setFotos] = useState([]);
  const [dadosFotos, setDadosFotos] = useState({});
  const [fasesFenologicas, setFasesFenologicas] = useState([]);
  const [gerandoPDF, setGerandoPDF] = useState(false);
  // Buscar ajustes de fases do banco e calcular fases fenológicas
  useEffect(() => {
    async function fetchFasesFenologicas() {
      if (!talhao?.id || !talhao?.cultura || !talhao?.data_plantio) {
        setFasesFenologicas([]);
        return;
      }
      try {
        const { data, error } = await supabase
          .from('fenologia_ajustada')
          .select('*')
          .eq('talhao_id', talhao.id);
        let datasAjustadas = {};
        if (!error && Array.isArray(data)) {
          data.forEach(row => {
            datasAjustadas[row.fase_idx] = {
              inicioFase: row.inicio,
              fimFase: row.fim
            };
          });
        }
        const fases = calcularFasesFenologicas(talhao.cultura, talhao.data_plantio, datasAjustadas);
        setFasesFenologicas(fases);
      } catch (e) {
        setFasesFenologicas([]);
      }
    }
    fetchFasesFenologicas();
  }, [talhao]);

  // Função para buscar dados de cada foto, incluindo fase fenológica
  useEffect(() => {
    async function fetchDadosFotos() {
      if (!talhao?.data_plantio || !talhao?.id || !Array.isArray(fotos) || fotos.length === 0) return;
      
      const dtPlantio = new Date(talhao.data_plantio);
      const dtColheita = talhao.data_colheita ? new Date(talhao.data_colheita) : null;
      const periodoMeteorologico = calcularPeriodoMeteorologicoFotos(talhao, fotos);
      
      // OTIMIZAÇÃO: Buscar dados meteorológicos UMA ÚNICA VEZ para todo o período
      let dadosMeteorologicos = [];
      try {
        if (periodoMeteorologico) {
          dadosMeteorologicos = await DadosMeteorologicosService.obterDadosMeteorologicos(
            talhao.id,
            talhao.coordenada_x || talhao.coordenadaX || talhao.longitude || 0,
            talhao.coordenada_y || talhao.coordenadaY || talhao.latitude || 0,
            periodoMeteorologico.dataInicio,
            periodoMeteorologico.dataFim
          );
        }
      } catch (e) {
        console.error('Erro ao buscar dados meteorológicos:', e);
      }
      
      const novosDados = {};
      for (const foto of fotos) {
        if (!foto.data_foto) continue;
        const dtFoto = new Date(foto.data_foto);
        // Só calcula se a data da foto está no intervalo
        if (dtFoto >= dtPlantio && (!dtColheita || dtFoto <= dtColheita)) {
          // Dias após plantio
          const dias_pos_plantio = Math.floor((dtFoto - dtPlantio) / (1000 * 60 * 60 * 24));
          
          // Filtrar dados meteorológicos até a data da foto
          let chuva_acumulada = '-';
          let evapotranspiracao_acumulada = '-';
          if (Array.isArray(dadosMeteorologicos) && dadosMeteorologicos.length > 0) {
            const dadosAteFoto = dadosMeteorologicos.filter(d => {
              const dtDado = new Date(d.data);
              return dtDado <= dtFoto;
            });
            if (dadosAteFoto.length > 0) {
              chuva_acumulada = dadosAteFoto.reduce((soma, d) => soma + (Number(d.precipitacao) || 0), 0).toFixed(1);
              evapotranspiracao_acumulada = dadosAteFoto.reduce((soma, d) => soma + (Number(d.et0) || 0), 0).toFixed(1);
            }
          }
          
          // Calcula fase fenológica vigente na data da foto
          let fase_fenologica = '-';
          if (fasesFenologicas && fasesFenologicas.length > 0) {
            fase_fenologica = obterFasePorData(fasesFenologicas, dtFoto);
          }
          novosDados[foto.id] = { dias_pos_plantio, chuva_acumulada, evapotranspiracao_acumulada, fase_fenologica };
        } else {
          novosDados[foto.id] = { dias_pos_plantio: '-', chuva_acumulada: '-', evapotranspiracao_acumulada: '-', fase_fenologica: '-' };
        }
      }
      setDadosFotos(novosDados);
    }
    fetchDadosFotos();
  }, [fotos, talhao, fasesFenologicas]);

  const [novaFoto, setNovaFoto] = useState(null);
  const [isCapacitor, setIsCapacitor] = useState(false);
  const [offlinePhotos, setOfflinePhotos] = useState([]);

  useEffect(() => {
    setIsCapacitor(!!(window && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()));
  }, []);

  // Carrega fotos pendentes da fila offline para este talhão
  useEffect(() => {
    function loadOfflinePhotos() {
      if (!talhao?.id) return;
      const pending = getQueue()
        .filter(i => i.status === 'pending' && i.type === 'photo' && i.talhaoId === talhao.id
          && (plantioScopeId(talhao) == null || i.plantioId == null || String(i.plantioId) === String(plantioScopeId(talhao))))
        .map(item => ({
          id: item.id,
          _offline: true,
          signedUrl: `data:image/${item.photoFormat};base64,${item.photoBase64}`,
          data_foto: item.dataFoto,
          observacao: item.observacao,
        }));
      setOfflinePhotos(pending);
    }
    loadOfflinePhotos();
    window.addEventListener(QUEUE_UPDATED_EVENT, loadOfflinePhotos);
    return () => window.removeEventListener(QUEUE_UPDATED_EVENT, loadOfflinePhotos);
  }, [talhao?.id]);
  const [observacao, setObservacao] = useState('');
  const [uploading, setUploading] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editData, setEditData] = useState({
    observacao: '',
    data_foto: '',
    fase_fenologica: ''
  });
  const [fotoAmpliada, setFotoAmpliada] = useState(null);

  useEffect(() => {
    async function fetchFotos() {
      if (!isOnline) return; // Offline: mostra apenas fotos da fila local
      const { data, error } = await supabase
        .from('fotos_talhao')
        .select('*')
        .eq('talhao_id', talhao.id)
        .order('data_foto', { ascending: true });
      if (!error && Array.isArray(data)) {
        const fotosDoPlantio = data.filter(f => fotoPertenceAoPlantio(f, talhao));
        console.log('📸 Carregando', fotosDoPlantio.length, 'fotos do plantio');
        const fotosComUrl = await Promise.all(fotosDoPlantio.map(async f => {
          let signedUrl = null;
          if (f.foto_path) {
            const { data: urlData, error: urlError } = await supabase.storage
              .from('fotos-talhao')
              .createSignedUrl(f.foto_path, 60 * 60 * 2); // 2 horas
            if (urlError) {
              console.error('❌ Erro ao gerar URL assinada para foto', f.id, ':', urlError);
            } else {
              signedUrl = urlData?.signedUrl;
              console.log('✅ URL assinada gerada para foto', f.id);
            }
          }
          return { ...f, signedUrl };
        }));
        setFotos(fotosComUrl);
        console.log('✅ Total de fotos carregadas:', fotosComUrl.length);
      } else {
        if (error) console.error('❌ Erro ao buscar fotos:', error);
        setFotos([]);
      }
    }
    if (talhao?.id) fetchFotos();
  }, [talhao, isOnline]);

  // Função para drag & drop (desktop)
  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    
    if (!isDesktop || !talhao?.id) return;
    
    const files = Array.from(e.dataTransfer.files);
    const imageFiles = files.filter(f => f.type.startsWith('image/'));
    
    if (imageFiles.length === 0) {
      alert('Nenhuma imagem foi detectada nos arquivos arrastados.');
      return;
    }
    
    for (const file of imageFiles) {
      setUploading(true);
      const fileName = `foto_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.jpg`;
      const compressed = await compressImage(file);
      
      const { error: uploadError } = await supabase.storage
        .from('fotos-talhao')
        .upload(fileName, compressed);
      
      if (uploadError) {
        console.error('Erro ao fazer upload:', uploadError);
        alert('Erro ao fazer upload: ' + uploadError.message);
        setUploading(false);
        continue;
      }
      
      const { error: insertError } = await supabase.from('fotos_talhao').insert([{
        talhao_id: talhao.id,
        foto_path: fileName,
        data_foto: localNoonUTC(),
        user_id: user.id,
        plantio_id: plantioScopeId(talhao)
      }]);
      
      if (insertError) {
        console.error('Erro ao salvar metadata:', insertError);
        alert('Erro ao salvar foto: ' + insertError.message);
      }
      
      setUploading(false);
    }
    
    // Recarrega fotos
    const { data } = await supabase
      .from('fotos_talhao')
      .select('*')
      .eq('talhao_id', talhao.id)
      .order('data_foto', { ascending: true });
      
    if (data) {
      const fotosComUrl = await Promise.all(data.filter(f => fotoPertenceAoPlantio(f, talhao)).map(async f => {
        let signedUrl = null;
        if (f.foto_path) {
          const { data: urlData } = await supabase.storage
            .from('fotos-talhao')
            .createSignedUrl(f.foto_path, 60 * 60);
          signedUrl = urlData?.signedUrl;
        }
        return { ...f, signedUrl };
      }));
      setFotos(fotosComUrl);
    }
  };
  
  const handleDragOver = (e) => {
    e.preventDefault();
    if (isDesktop) setDragOver(true);
  };
  
  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOver(false);
  };

  async function handleUploadFoto() {
    console.log('[AlbumFotos] handleUploadFoto chamado', { talhao, user, isCapacitor });
    if (!talhao?.id) {
      console.warn('[AlbumFotos] handleUploadFoto: talhao.id ausente');
      return;
    }
    setUploading(true);
    console.log('[AlbumFotos] setUploading true');
    let file, fileName;
    if (isCapacitor && Camera) {
      console.log('[AlbumFotos] Usando Camera do Capacitor');
      try {
        const photo = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: 'base64',
          source: 'PROMPT',
        });
        if (!photo || !photo.base64String) throw new Error('Nenhuma foto selecionada');
        const base64 = photo.base64String;
        const format = photo.format || 'jpeg';
        fileName = `foto_${Date.now()}.jpg`;
        // Comprime via canvas redimensionando para max 1920px
        const compressedBlob = await compressBase64Image(base64, format);
        if (compressedBlob) {
          file = compressedBlob;
        } else {
          const byteString = atob(base64);
          const ab = new ArrayBuffer(byteString.length);
          const ia = new Uint8Array(ab);
          for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
          file = new Blob([ab], { type: `image/${format}` });
        }
      } catch (e) {
        setUploading(false);
        console.error('[AlbumFotos] Erro ao capturar foto:', e);
        alert('Erro ao capturar foto: ' + (e && e.message ? e.message : e));
        return;
      }
    } else {
      console.log('[AlbumFotos] Usando input file (web)');
      if (!novaFoto) {
        setUploading(false);
        console.warn('[AlbumFotos] handleUploadFoto: novaFoto ausente');
        return;
      }
      fileName = `foto_${Date.now()}.jpg`;
      file = await compressImage(novaFoto);
    }

    // ── OFFLINE: enfileira para envio posterior ──────────────────────────────
    if (!isOnline) {
      const base64 = await blobToBase64(file);
      addPhotoToQueue({
        talhaoId: talhao.id,
        plantioId: plantioScopeId(talhao),
        userId: user.id,
        observacao,
        dataFoto: localNoonUTC(),
        photoBase64: base64,
        photoFormat: 'jpeg',
      });
      setNovaFoto(null);
      setObservacao('');
      setUploading(false);
      console.log('[AlbumFotos] Foto salva na fila offline');
      return;
    }
    // ────────────────────────────────────────────────────────────────────────

    const { error: uploadError } = await supabase.storage
      .from('fotos-talhao')
      .upload(fileName, file);
    if (uploadError) {
      console.error('[AlbumFotos] Erro ao fazer upload da foto:', uploadError);
    } else {
      const { data: fotoData, error: insertError } = await supabase
        .from('fotos_talhao')
        .insert([
          {
            talhao_id: talhao.id,
            foto_path: fileName,
            observacao,
            data_foto: localNoonUTC(),
            user_id: user.id,
            plantio_id: plantioScopeId(talhao)
          }
        ])
        .select()
        .single();
      if (insertError) {
        console.error('[AlbumFotos] Erro ao inserir foto no banco:', insertError);
      } else {
        const newFoto = {
          ...(fotoData ?? { talhao_id: talhao.id, foto_path: fileName, observacao, data_foto: localNoonUTC() }),
          signedUrl: isCapacitor ? undefined : URL.createObjectURL(file),
        };
        setFotos(prev => [...prev, newFoto]);
        setNovaFoto(null);
        setObservacao('');
        console.log('[AlbumFotos] Foto inserida com sucesso:', newFoto);
      }
    }
    setUploading(false);
    console.log('[AlbumFotos] setUploading false');
  }

  function startEdit(foto) {
    setEditId(foto.id);
    setEditData({
      observacao: foto.observacao || '',
      data_foto: isoToLocalDate(foto.data_foto),
      fase_fenologica: foto.fase_fenologica || ''
    });
  }

  function cancelEdit() {
    setEditId(null);
    setEditData({
      observacao: '',
      data_foto: '',
      fase_fenologica: ''
    });
  }

  async function saveEdit(foto) {
    // Salva a data como timestamp de meio-dia UTC para evitar ambiguidade de fuso horário
    const dataFotoSave = editData.data_foto
      ? editData.data_foto + 'T12:00:00Z'
      : editData.data_foto;
    const { data: updated, error } = await supabase
      .from('fotos_talhao')
      .update({
        observacao: editData.observacao,
        data_foto: dataFotoSave,
      })
      .eq('id', foto.id)
      .select();
    if (error) {
      console.error('[AlbumFotos] Erro ao salvar edição:', error);
      alert('Erro ao salvar alterações. Tente novamente.');
      return;
    }
    // updated pode ser [] se RLS bloqueou o SELECT pós-update, mas o update em si funcionou
    const updatedFoto = (updated && updated.length > 0) ? updated[0] : { observacao: editData.observacao, data_foto: dataFotoSave };
    setFotos(prev => prev.map(f =>
      f.id === foto.id ? { ...f, ...updatedFoto } : f
    ));
    cancelEdit();
  }

  async function handleDeleteFoto(fotoId, fotoPath, isOffline) {
    if (isOffline) {
      if (!window.confirm('Remover foto pendente?')) return;
      removeFromQueue(fotoId);
      return;
    }
    const { error } = await supabase
      .from('fotos_talhao')
      .delete()
      .eq('id', fotoId);
    if (!error) {
      setFotos(prev => prev.filter(f => f.id !== fotoId));
      if (fotoPath) {
        const { error: storageError } = await supabase.storage
          .from('fotos-talhao')
          .remove([fotoPath]);
      }
    }
  }

  async function urlToBase64(url) {
    console.log('🖼️ Convertendo imagem para base64:', url);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        console.error('❌ Erro ao fazer fetch da imagem:', response.status, response.statusText);
        throw new Error(`Falha ao carregar imagem: ${response.status}`);
      }
      const blob = await response.blob();
      console.log('✅ Imagem carregada. Tipo:', blob.type, 'Tamanho:', blob.size);
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const base64String = reader.result;
          // Se for uma imagem JPEG/JPG, mantém em alta qualidade
          // Se for PNG, converte para JPEG de alta qualidade para reduzir tamanho do PDF
          if (blob.type === 'image/png') {
            const img = new Image();
            img.onload = () => {
              const canvas = document.createElement('canvas');
              canvas.width = img.width;
              canvas.height = img.height;
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, 0, 0);
              // Converte para JPEG com qualidade 0.95 (95%)
              const jpegBase64 = canvas.toDataURL('image/jpeg', 0.95);
              console.log('✅ Imagem PNG convertida para JPEG');
              resolve(jpegBase64);
            };
            img.onerror = () => {
              console.error('❌ Erro ao carregar imagem para conversão');
              reject(new Error('Falha ao processar imagem PNG'));
            };
            img.src = base64String;
          } else {
            console.log('✅ Imagem convertida para base64');
            resolve(base64String);
          }
        };
        reader.onerror = () => {
          console.error('❌ Erro ao ler blob da imagem');
          reject(new Error('Falha ao ler arquivo de imagem'));
        };
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error('❌ Erro na função urlToBase64:', error);
      throw error;
    }
  }

  async function handleExportPDF() {
    // Verifica se o usuário tem permissão para gerar relatórios PDF
    if (!podeUsar('relatorios_pdf')) {
      setShowUpgradeModal(true);
      return;
    }
    
    setGerandoPDF(true);
    try {
      // Verifica e solicita permissões no Android se necessário
      if (Capacitor.isNativePlatform() && Filesystem) {
        try {
          const { publicStorage } = await Filesystem.checkPermissions();
          if (publicStorage !== 'granted') {
            const result = await Filesystem.requestPermissions();
            if (result.publicStorage !== 'granted') {
              alert('Permissão de armazenamento negada. Por favor, permita o acesso aos arquivos nas configurações do app.');
              setGerandoPDF(false);
              return;
            }
          }
        } catch (err) {
          console.log('Erro ao verificar permissões:', err);
        }
      }

    // Calcula safra pela data de plantio
    let safraCalculada = '-';
    if (talhao.data_plantio) {
      const dataPlantio = new Date(talhao.data_plantio);
      const ano = dataPlantio.getFullYear();
      const mes = dataPlantio.getMonth() + 1; // Mês de 1-12
      // Se plantio entre julho e dezembro, safra é ano/ano+1
      // Se plantio entre janeiro e junho, safra é ano-1/ano
      if (mes >= 7) {
        safraCalculada = `${ano}/${ano + 1}`;
      } else {
        safraCalculada = `${ano - 1}/${ano}`;
      }
    }

    // Converte as imagens para base64 e busca chuva/evapotranspiração acumuladas
    // OTIMIZAÇÃO: Buscar dados meteorológicos UMA ÚNICA VEZ para todo o período
    let dadosMeteorologicos = [];
    if (talhao.data_plantio && talhao.id) {
      const periodoMeteorologico = calcularPeriodoMeteorologicoFotos(talhao, fotos);
      try {
        if (periodoMeteorologico) {
          dadosMeteorologicos = await DadosMeteorologicosService.obterDadosMeteorologicos(
            talhao.id,
            talhao.coordenada_x || talhao.coordenadaX || talhao.longitude || 0,
            talhao.coordenada_y || talhao.coordenadaY || talhao.latitude || 0,
            periodoMeteorologico.dataInicio,
            periodoMeteorologico.dataFim
          );
        }
      } catch (e) {
        console.error('Erro ao buscar dados meteorológicos para PDF:', e);
      }
    }
    
    const fotosBase64 = [];
    console.log('📸 Processando', fotos.length, 'fotos para o PDF...');
    for (const foto of fotos) {
      console.log('📷 Processando foto:', foto.id, 'URL:', foto.signedUrl ? 'disponível' : 'não disponível');
      let base64 = null;
      if (foto.signedUrl) {
        try {
          base64 = await urlToBase64(foto.signedUrl);
          console.log('✅ Foto convertida para base64');
        } catch (error) {
          console.error('❌ Erro ao converter foto para base64:', error);
          // Continua com as outras fotos
        }
      } else {
        console.warn('⚠️ Foto sem URL assinada:', foto.id);
      }

      // Busca chuva e evapotranspiração acumuladas usando dados já carregados
      let chuva_acumulada = '-';
      let evapotranspiracao_acumulada = '-';
      let dias_pos_plantio = '-';
      let fase_fenologica = '-';
      
      try {
        if (talhao.data_plantio && foto.data_foto) {
          // Calcular dias após plantio
          const dtPlantio = new Date(talhao.data_plantio);
          const dtFoto = new Date(foto.data_foto);
          const diffMs = dtFoto - dtPlantio;
          if (!isNaN(diffMs)) {
            dias_pos_plantio = Math.floor(diffMs / (1000 * 60 * 60 * 24));
          }

          // Calcula fase fenológica vigente na data da foto
          if (fasesFenologicas && fasesFenologicas.length > 0) {
            fase_fenologica = obterFasePorData(fasesFenologicas, dtFoto);
          }

          // Filtrar dados meteorológicos até a data da foto
          if (Array.isArray(dadosMeteorologicos) && dadosMeteorologicos.length > 0) {
            const dadosAteFoto = dadosMeteorologicos.filter(d => {
              const dtDado = new Date(d.data);
              return dtDado <= dtFoto;
            });
            if (dadosAteFoto.length > 0) {
              chuva_acumulada = dadosAteFoto.reduce((soma, d) => soma + (Number(d.precipitacao) || 0), 0).toFixed(1);
              evapotranspiracao_acumulada = dadosAteFoto.reduce((soma, d) => soma + (Number(d.et0) || 0), 0).toFixed(1);
            }
          }
        }
      } catch (e) {
        // Se der erro, mantém '-'
      }

      fotosBase64.push({
        ...foto,
        base64,
        chuva_acumulada,
        evapotranspiracao_acumulada,
        dias_pos_plantio,
        fase_fenologica
      });
    }
    
    console.log('✅ Total de fotos processadas:', fotosBase64.length);
    console.log('📄 Iniciando montagem do documento PDF...');

    // Carrega o logo como base64
    async function getLogoBase64() {
      const response = await fetch('/ClimoraX_vertical.png');
      const blob = await response.blob();
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });
    }
    const logoBase64 = await getLogoBase64();

    // Monta as fotos em grid de 3 colunas (3x2 = 6 fotos por página)
    const fotoRows = [];
    for (let i = 0; i < fotosBase64.length; i += 3) {
      const row = [];
      for (let j = 0; j < 3; j++) {
        const foto = fotosBase64[i + j];
        if (foto) {
          row.push({
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    stack: [
                      // Data no topo com destaque
                      foto.data_foto ? {
                        text: new Date(foto.data_foto.length === 10 ? foto.data_foto + 'T12:00:00' : foto.data_foto).toLocaleDateString('pt-BR'),
                        alignment: 'center',
                        color: '#1976d2',
                        fontSize: 10,
                        bold: true,
                        margin: [0, 0, 0, 4]
                      } : {},
                      // Imagem otimizada - reduzida para caber 3 por linha
                      foto.base64 ? {
                        image: foto.base64,
                        width: 140,
                        alignment: 'center',
                        margin: [0, 0, 0, 6],
                        fit: [140, 140]
                      } : {},
                      // Informações organizadas profissionalmente
                      {
                        table: {
                          widths: ['auto', '*'],
                          body: [
                            [
                              { text: 'Fase:', fontSize: 7, color: '#666', bold: true, border: [false, false, false, false] },
                              { text: foto.fase_fenologica || 'Não definida', fontSize: 7, color: '#222', border: [false, false, false, false] }
                            ],
                            [
                              { text: 'Dias:', fontSize: 7, color: '#666', bold: true, border: [false, false, false, false] },
                              { text: `${foto.dias_pos_plantio ?? '-'}`, fontSize: 7, color: '#222', border: [false, false, false, false] }
                            ],
                            [
                              { text: 'Chuva:', fontSize: 7, color: '#666', bold: true, border: [false, false, false, false] },
                              { text: `${foto.chuva_acumulada || '-'} mm`, fontSize: 7, color: '#222', border: [false, false, false, false] }
                            ],
                            [
                              { text: 'ET0:', fontSize: 7, color: '#666', bold: true, border: [false, false, false, false] },
                              { text: `${foto.evapotranspiracao_acumulada || '-'} mm`, fontSize: 7, color: '#222', border: [false, false, false, false] }
                            ]
                          ]
                        },
                        layout: {
                          hLineWidth: () => 0,
                          vLineWidth: () => 0,
                          paddingLeft: () => 0,
                          paddingRight: () => 4,
                          paddingTop: () => 1,
                          paddingBottom: () => 1
                        },
                        margin: [0, 0, 0, 4]
                      },
                      // Observação compacta
                      foto.observacao ? {
                        stack: [
                          {
                            text: 'OBS.',
                            fontSize: 6,
                            color: '#22cb22',
                            bold: true,
                            margin: [0, 2, 0, 1]
                          },
                          {
                            text: foto.observacao.length > 80 ? foto.observacao.substring(0, 80) + '...' : foto.observacao,
                            fontSize: 6,
                            color: '#444',
                            lineHeight: 1.2,
                            margin: [0, 0, 0, 0]
                          }
                        ],
                        margin: [0, 0, 0, 0]
                      } : {}
                    ],
                    margin: [0, 0, 0, 0]
                  }
                ]
              ]
            },
            layout: {
              hLineWidth: () => 0,
              vLineWidth: () => 0,
              paddingLeft: () => 6,
              paddingRight: () => 6,
              paddingTop: () => 8,
              paddingBottom: () => 8,
              fillColor: () => '#ffffff'
            },
            margin: [0, 0, 0, 10]
          });
        } else {
          row.push({}); // célula vazia para completar a linha
        }
      }
      fotoRows.push(row);
    }

    // Documento PDF com layout otimizado para 6 fotos por página
    const docDefinition = {
      pageSize: 'A4',
      pageMargins: [30, 20, 30, 30],
      defaultStyle: {
        font: 'Roboto'
      },
      pageBreakBefore: function(currentNode, followingNodesOnPage, nodesOnNextPage, previousNodesOnPage) {
        return currentNode.headlineLevel === 1 && followingNodesOnPage.length === 0;
      },
      content: [
        // Header com logo vertical ClimoraX
        {
          columns: [
            {
              image: logoBase64,
              width: 85,
              height: 85,
              margin: [0, 0, 8, 0]
            },
            {
              stack: [
                {
                  text: 'Relatório Fotográfico',
                  color: '#003B73',
                  fontSize: 14,
                  bold: true,
                  margin: [0, 0, 0, 2]
                },
                {
                  text: new Date().toLocaleDateString('pt-BR') + '  ' + new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
                  color: '#777',
                  fontSize: 8,
                  margin: [0, 0, 0, 0]
                }
              ],
              margin: [0, 28, 0, 0]
            },
            { text: '', width: '*' }
          ],
          margin: [0, 0, 0, 6]
        },
        // Linha separadora elegante
        {
          canvas: [
            {
              type: 'line',
              x1: 0, y1: 0,
              x2: 535, y2: 0,
              lineWidth: 1,
              lineColor: '#e0e0e0'
            }
          ],
          margin: [0, 0, 0, 12]
        },
        // Informações do talhão com design clean
        {
          table: {
            widths: ['25%', '25%', '25%', '25%'],
            body: [
              [
                ...(talhao.propriedade_nome ? [
                  { text: 'PROPRIEDADE', fontSize: 8, color: '#999', bold: true, border: [false, false, false, true], borderColor: ['#fff', '#fff', '#fff', '#e0e0e0'], margin: [0, 0, 0, 2] }
                ] : [
                  { text: 'TALHÃO', fontSize: 8, color: '#999', bold: true, border: [false, false, false, true], borderColor: ['#fff', '#fff', '#fff', '#e0e0e0'], margin: [0, 0, 0, 2] }
                ]),
                { text: 'CULTURA', fontSize: 8, color: '#999', bold: true, border: [false, false, false, true], borderColor: ['#fff', '#fff', '#fff', '#e0e0e0'], margin: [0, 0, 0, 2] },
                { text: 'SAFRA', fontSize: 8, color: '#999', bold: true, border: [false, false, false, true], borderColor: ['#fff', '#fff', '#fff', '#e0e0e0'], margin: [0, 0, 0, 2] },
                { text: 'ÁREA', fontSize: 8, color: '#999', bold: true, border: [false, false, false, true], borderColor: ['#fff', '#fff', '#fff', '#e0e0e0'], margin: [0, 0, 0, 2] }
              ],
              [
                ...(talhao.propriedade_nome ? [
                  { text: talhao.propriedade_nome, fontSize: 10, color: '#222', bold: true, border: [false, false, false, false], margin: [0, 4, 0, 0] }
                ] : [
                  { text: talhao.nome || '-', fontSize: 10, color: '#222', bold: true, border: [false, false, false, false], margin: [0, 4, 0, 0] }
                ]),
                { text: talhao.cultura || '-', fontSize: 10, color: '#222', border: [false, false, false, false], margin: [0, 4, 0, 0] },
                { text: safraCalculada, fontSize: 10, color: '#222', border: [false, false, false, false], margin: [0, 4, 0, 0] },
                { text: (talhao.area ? talhao.area + ' ha' : '-'), fontSize: 10, color: '#222', border: [false, false, false, false], margin: [0, 4, 0, 0] }
              ]
            ]
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 12,
            paddingTop: () => 0,
            paddingBottom: () => 6,
            fillColor: () => '#fafafa'
          },
          margin: [0, 0, 0, 14]
        },
        { 
          text: 'REGISTRO FOTOGRÁFICO', 
          color: '#666', 
          fontSize: 11, 
          bold: true, 
          letterSpacing: 1,
          margin: [0, 0, 0, 10] 
        },
        {
          table: {
            widths: ['*', '*', '*'],
            body: fotoRows
          },
          layout: {
            hLineWidth: () => 0,
            vLineWidth: (i, node) => (i > 0 && i < node.table.widths.length) ? 0.5 : 0,
            vLineColor: () => '#e8e8e8',
            paddingLeft: () => 0,
            paddingRight: () => 0,
            paddingTop: () => 0,
            paddingBottom: () => 0
          },
          margin: [0, 0, 0, 0]
        }
      ],
      footer: function(currentPage, pageCount) {
        return {
          text: [
            { text: 'Gerado em ', fontSize: 8, color: '#999' },
            { text: new Date().toLocaleString('pt-BR'), fontSize: 8, color: '#666', bold: true },
            { text: ' • ClimoraX', fontSize: 8, color: '#003B73', bold: true }
          ],
          alignment: 'center',
          margin: [0, 15, 0, 15]
        };
      }
    };

  console.log('📄 Iniciando geração de PDF. Fotos disponíveis:', fotos.length);
  console.log('👤 Permissão do usuário:', permission);
  console.log('📱 isCapacitor:', Capacitor.isNativePlatform());

  // Gera nome do arquivo com nome do talhão e data
  const dataAtual = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
  const nomeTalhao = (talhao.nome || 'talhao').replace(/[^a-zA-Z0-9_-]/g, '_');
  const nomeArquivo = `relatorio_fotografico_${nomeTalhao}_${dataAtual}.pdf`;

  let isCapacitor = false;
  try {
    isCapacitor = typeof window !== 'undefined' && window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform();
  } catch (e) {
    isCapacitor = false;
  }
  
  console.log('📱 É dispositivo móvel?', isCapacitor);
  console.log('💾 Filesystem disponível?', !!Filesystem);
  console.log('📂 Directory disponível?', !!Directory);
  
  if (isCapacitor && Filesystem && Directory) {
    console.log('✅ Tentando salvar no dispositivo móvel...');
    try {
      pdfMake.createPdf(docDefinition).getBase64(async (pdfBase64) => {
        console.log('📄 PDF convertido para base64. Tamanho:', pdfBase64.length);
        try {
          console.log('💾 Tentando escrever arquivo:', nomeArquivo);
          // Salva na pasta Downloads (mais acessível)
          const result = await Filesystem.writeFile({
            path: nomeArquivo,
            data: pdfBase64,
            directory: Directory.Documents,
          });
          
          console.log('✅ PDF salvo com sucesso:', result.uri);
          
          // Tenta abrir o arquivo automaticamente
          if (FileOpener && result.uri) {
            console.log('📂 Tentando abrir arquivo automaticamente...');
            try {
              await FileOpener.open({
                filePath: result.uri,
                contentType: 'application/pdf',
              });
              alert(`Relatório salvo com sucesso!\n\nArquivo: ${nomeArquivo}\n\nSe não abrir automaticamente, procure na pasta Documentos do seu gerenciador de arquivos.`);
            } catch (openErr) {
              console.log('⚠️ Não foi possível abrir automaticamente:', openErr);
              alert(`PDF salvo com sucesso!\n\nArquivo: ${nomeArquivo}\n\nProcure na pasta "Documentos" ou "Documents" do seu gerenciador de arquivos.\n\nCaminho: ${result.uri}`);
            }
          } else {
            console.log('ℹ️ FileOpener não disponível');
            alert(`PDF salvo com sucesso!\n\nArquivo: ${nomeArquivo}\n\nProcure na pasta "Documentos" ou "Documents" do seu gerenciador de arquivos.`);
          }
          
          setGerandoPDF(false);
        } catch (err) {
          console.error('❌ Erro ao salvar PDF no dispositivo:', err);
          console.error('Detalhes do erro:', JSON.stringify(err));
          alert('Erro ao salvar PDF no dispositivo: ' + (err && err.message ? err.message : JSON.stringify(err)));
          setGerandoPDF(false);
        }
      }, (err) => {
        // Callback de erro do getBase64
        console.error('❌ Erro ao converter PDF para base64:', err);
        alert('Erro ao converter PDF: ' + (err && err.message ? err.message : JSON.stringify(err)));
        setGerandoPDF(false);
      });
    } catch (err) {
      console.error('❌ Erro ao gerar PDF:', err);
      alert('Erro ao gerar PDF: ' + (err && err.message ? err.message : JSON.stringify(err)));
      setGerandoPDF(false);
    }
  } else {
    try {
      pdfMake.createPdf(docDefinition).download(nomeArquivo);
      console.log('PDF baixado normalmente no navegador.');
      setGerandoPDF(false);
    } catch (err) {
      console.error('Erro ao baixar PDF no navegador:', err);
      alert('Erro ao baixar PDF: ' + (err && err.message ? err.message : err));
      setGerandoPDF(false);
    }
  }
    } catch (error) {
      console.error('Erro geral ao gerar PDF:', error);
      setGerandoPDF(false);
    }
  }

  // Fim da função handleExportPDF
  // O restante do componente React continua normalmente abaixo

  const todasFotos = [...fotos, ...offlinePhotos];
  const datasValidas = todasFotos
    .map(foto => foto.data_foto ? new Date(foto.data_foto.length === 10 ? `${foto.data_foto}T12:00:00` : foto.data_foto) : null)
    .filter(data => data && !Number.isNaN(data.getTime()));
  const dataMaisRecente = datasValidas.length
    ? new Date(Math.max(...datasValidas.map(data => data.getTime())))
    : null;
  const inputFotoId = `plot-photo-input-${talhao?.id || 'current'}`;

return (
  <div 
    className={`plot-photo-workspace${darkMode ? ' is-dark' : ''}`}
    style={{ 
      maxWidth: 480, 
      margin: '32px auto', 
      background: darkMode ? '#1a1a2e' : '#f8fbfd', 
      borderRadius: 24, 
      boxShadow: darkMode ? '0 2px 16px #0006' : '0 2px 16px #eee', 
      padding: 16,
      position: 'relative'
    }}
    onDrop={handleDrop}
    onDragOver={handleDragOver}
    onDragLeave={handleDragLeave}
  >
    {/* Overlay de drag & drop (desktop) */}
    {dragOver && isDesktop && (
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(25, 118, 210, 0.9)',
        borderRadius: 24,
        zIndex: 1000,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontSize: 24,
        fontWeight: 700,
        pointerEvents: 'none'
      }}>
        <div>📸 Solte as fotos aqui</div>
        <div style={{ fontSize: 14, marginTop: 8, opacity: 0.9 }}>Arraste e solte imagens para fazer upload</div>
      </div>
    )}
    
    <header className="plot-photo-header">
      <div>
        <span className="plot-photo-eyebrow">Memória do talhão</span>
        <h2>Registro fotográfico</h2>
        <p>Documente a evolução da cultura com imagens, observações e contexto agronômico.</p>
      </div>
      <span className={`plot-photo-access-badge ${podeEditar ? 'can-edit' : 'read-only'}`}>
        {podeEditar ? '● Edição habilitada' : 'Somente leitura'}
      </span>
    </header>

    <section className="plot-photo-summary" aria-label="Resumo do registro fotográfico">
      <article>
        <span>Fotos registradas</span>
        <strong>{todasFotos.length}</strong>
        <small>neste talhão</small>
      </article>
      <article>
        <span>Registro mais recente</span>
        <strong>{dataMaisRecente ? dataMaisRecente.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' }) : '—'}</strong>
        <small>{dataMaisRecente ? dataMaisRecente.getFullYear() : 'sem registros'}</small>
      </article>
      <article className={offlinePhotos.length ? 'has-pending' : ''}>
        <span>Sincronização</span>
        <strong>{offlinePhotos.length ? `${offlinePhotos.length} pendente${offlinePhotos.length > 1 ? 's' : ''}` : 'Em dia'}</strong>
        <small>{isOnline ? 'conectado' : 'modo offline'}</small>
      </article>
    </section>

    {/* Dica de drag & drop para desktop */}
    {isDesktop && fotos.length > 0 && (
      <div style={{
        background: darkMode ? '#1e3a5f' : '#e3f2fd',
        padding: '8px 12px',
        borderRadius: 8,
        marginBottom: 12,
        textAlign: 'center',
        fontSize: 13,
        color: darkMode ? '#90caf9' : '#1976d2'
      }}>
        💡 <strong>Dica:</strong> Arraste fotos diretamente para esta área!
      </div>
    )}

    {/* Formulário de upload de foto */}
    {podeEditar && editId === null && (
      <form className="plot-photo-upload" onSubmit={e => { e.preventDefault(); handleUploadFoto(); }}>
        <div className="plot-photo-upload-heading">
          <span className="plot-photo-upload-icon" aria-hidden="true">📷</span>
          <div>
            <h3>Adicionar novo registro</h3>
            <p>Imagens são compactadas automaticamente antes do envio.</p>
          </div>
        </div>
        <div className="plot-photo-upload-fields">
          {isCapacitor && Camera ? (
            <button className="plot-photo-picker mobile" type="button" onClick={handleUploadFoto} disabled={uploading}>
              {uploading ? 'Enviando...' : '📷 Tirar ou escolher foto'}
            </button>
          ) : (
            <div className={`plot-photo-file-field${novaFoto ? ' has-file' : ''}`}>
              <input id={inputFotoId} type="file" accept="image/*" onChange={e => setNovaFoto(e.target.files[0] || null)} />
              <label htmlFor={inputFotoId} className="plot-photo-picker">
                <span aria-hidden="true">＋</span>
                <strong>{novaFoto ? 'Trocar imagem' : 'Escolher imagem'}</strong>
              </label>
              <div>
                <strong>{novaFoto ? novaFoto.name : 'Nenhuma imagem selecionada'}</strong>
                <small>JPG, PNG ou imagem da câmera</small>
              </div>
            </div>
          )}
          <label className="plot-photo-note-field">
            <span>Observação de campo</span>
            <textarea placeholder="Descreva o manejo, a ocorrência ou o ponto observado na imagem..." value={observacao} onChange={e => setObservacao(e.target.value)} rows="3" />
          </label>
          {!isCapacitor && (
            <button className="plot-photo-submit" type="submit" disabled={uploading || !novaFoto}>
              {uploading ? 'Enviando registro...' : 'Salvar foto no histórico'}
            </button>
          )}
        </div>
      </form>
    )}

    {/* Formulário de edição de foto */}
    {podeEditar && editId !== null && (
      <form className="plot-photo-edit" onSubmit={e => { e.preventDefault(); saveEdit(fotos.find(f => f.id === editId)); }}>
        <div className="plot-photo-edit-heading">
          <div><span>Editar registro</span><strong>Atualize a data e a observação da foto</strong></div>
          <button type="button" onClick={cancelEdit} aria-label="Cancelar edição">✕</button>
        </div>
        <div className="plot-photo-edit-fields">
          <label><span>Observação</span><textarea value={editData.observacao} onChange={e => setEditData({ ...editData, observacao: e.target.value })} rows="3" /></label>
          <label><span>Data do registro</span><input type="date" value={editData.data_foto} onChange={e => setEditData({ ...editData, data_foto: e.target.value })} /></label>
          <div className="plot-photo-edit-actions">
            <button type="button" onClick={cancelEdit}>Cancelar</button>
            <button type="submit">Salvar alterações</button>
          </div>
        </div>
      </form>
    )}

    <section className="plot-photo-gallery-section">
      <div className="plot-photo-gallery-heading">
        <div>
          <span>Linha do tempo visual</span>
          <h3>Galeria do talhão</h3>
          <p>{todasFotos.length ? `${todasFotos.length} registro${todasFotos.length > 1 ? 's' : ''} com contexto da cultura` : 'O histórico fotográfico começará com o primeiro registro.'}</p>
        </div>
        <button className="plot-photo-report-button" onClick={handleExportPDF} disabled={gerandoPDF}>
          {gerandoPDF ? 'Gerando relatório...' : podeUsarPdf ? '↧ Relatório fotográfico' : '🔒 Relatório PDF'}
        </button>
      </div>
      {/* Área de fotos */}
      {(fotos.length > 0 || offlinePhotos.length > 0) ? (
        <div className="plot-photo-grid">
          {[...fotos, ...offlinePhotos].map((foto, idx) => (
            <article className="plot-photo-card" key={foto.id || idx}>
              {/* Badge pré-plantio */}
              {(() => {
                const isPrePlantio = talhao.data_plantio && foto.data_foto &&
                  new Date(foto.data_foto.length === 10 ? foto.data_foto + 'T12:00:00' : foto.data_foto) <
                  new Date(talhao.data_plantio + 'T00:00:00');
                return isPrePlantio ? (
                  <div className="plot-photo-status pre-planting" style={{
                    background: '#e8f5e9', color: '#2e7d32', borderRadius: 6,
                    padding: '2px 8px', fontSize: 11, fontWeight: 700,
                    marginBottom: 6, textAlign: 'center', border: '1px solid #a5d6a7'
                  }}>
                    🌱 Pré-plantio
                  </div>
                ) : null;
              })()}
              {foto._offline && (
                <div className="plot-photo-status pending">
                  ⏳ Aguardando conexão
                </div>
              )}
              {foto.signedUrl && (
                <img 
                  className="plot-photo-image"
                  src={foto.signedUrl} 
                  alt="Foto" 
                  onClick={() => setFotoAmpliada(foto)}
                  style={{ 
                    width: '100%', 
                    height: 110, 
                    objectFit: 'cover', 
                    borderRadius: 8, 
                    marginBottom: 8,
                    cursor: 'pointer',
                    transition: 'transform 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                  onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                />
              )}
              <div className="plot-photo-date">
                <span aria-hidden="true">▣</span>
                {foto.data_foto ? new Date(foto.data_foto.length === 10 ? foto.data_foto + 'T12:00:00' : foto.data_foto).toLocaleDateString('pt-BR') : ''}
              </div>
              <div className="plot-photo-observation">
                <span>Observação de campo</span>
                <p>{foto.observacao || 'Nenhuma observação adicionada a este registro.'}</p>
              </div>
              {(() => {
                const isPrePlantio = talhao.data_plantio && foto.data_foto &&
                  new Date(foto.data_foto.length === 10 ? foto.data_foto + 'T12:00:00' : foto.data_foto) <
                  new Date(talhao.data_plantio + 'T00:00:00');
                if (isPrePlantio) return null;
                return (
                  <>
                    <div className="plot-photo-metadata">
                      <span aria-hidden="true">🌱</span>
                      <div><small>Fase fenológica</small><strong>{dadosFotos[foto.id]?.fase_fenologica || 'Não identificada'}</strong></div>
                    </div>
                    <div className="plot-photo-metadata">
                      <span aria-hidden="true">◷</span>
                      <div><small>Idade da cultura</small><strong>
                        {dadosFotos[foto.id]?.dias_pos_plantio !== undefined && dadosFotos[foto.id]?.dias_pos_plantio !== '-' ? `${dadosFotos[foto.id].dias_pos_plantio} dias após plantio` : '-'}
                      </strong></div>
                    </div>
                    <div className="plot-photo-metadata">
                      <span aria-hidden="true">☔</span>
                      <div><small>Chuva acumulada</small><strong>{dadosFotos[foto.id]?.chuva_acumulada !== undefined && dadosFotos[foto.id]?.chuva_acumulada !== '-' ? `${dadosFotos[foto.id].chuva_acumulada} mm` : 'Sem dados'}</strong></div>
                    </div>
                    <div className="plot-photo-metadata">
                      <span aria-hidden="true">💧</span>
                      <div><small>Evapotranspiração</small><strong>{dadosFotos[foto.id]?.evapotranspiracao_acumulada !== undefined && dadosFotos[foto.id]?.evapotranspiracao_acumulada !== '-' ? `${dadosFotos[foto.id].evapotranspiracao_acumulada} mm` : 'Sem dados'}</strong></div>
                    </div>
                  </>
                );
              })()}
              {podeEditar && (
                <div className="plot-photo-actions">
                  {!foto._offline && <button className="edit" onClick={() => startEdit(foto)}>✎ Editar registro</button>}
                  <button className="delete" onClick={() => handleDeleteFoto(foto.id, foto.foto_path, foto._offline)}>⌫ Excluir</button>
                </div>
              )}
            </article>
          ))}
        </div>
      ) : (
        <div className="plot-photo-empty"><span>▧</span><strong>Nenhuma foto cadastrada</strong><p>Adicione o primeiro registro para iniciar a linha do tempo visual deste talhão.</p></div>
      )}
    </section>
    {/* Modal de Upgrade */}
    {showUpgradeModal && (
      <UpgradeModal 
        onClose={() => setShowUpgradeModal(false)}
        feature="Relatórios em PDF"
        description="Gere relatórios fotográficos profissionais em PDF para documentar o desenvolvimento das suas lavouras."
        user={user}
      />
    )}

    {/* Modal de visualização da foto em tamanho maior */}
    {fotoAmpliada && (
      <div 
        className="plot-photo-lightbox"
        onClick={() => setFotoAmpliada(null)}
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 9999,
          cursor: 'pointer',
          padding: 20
        }}
      >
        <div 
          className="plot-photo-lightbox-content"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'relative',
            maxWidth: '90vw',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}
        >
          <button
            className="plot-photo-lightbox-close"
            onClick={() => setFotoAmpliada(null)}
            aria-label="Fechar foto ampliada"
            style={{
              position: 'absolute',
              top: -10,
              right: -10,
              background: '#fff',
              border: 'none',
              borderRadius: '50%',
              width: 36,
              height: 36,
              fontSize: 20,
              cursor: 'pointer',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 10000
            }}
          >
            ✕
          </button>
          <img 
            className="plot-photo-lightbox-image"
            src={fotoAmpliada.signedUrl} 
            alt="Foto ampliada"
            style={{
              maxWidth: '100%',
              maxHeight: '85vh',
              objectFit: 'contain',
              borderRadius: 12,
              boxShadow: '0 4px 20px rgba(0,0,0,0.5)'
            }}
          />
          {fotoAmpliada.data_foto && (
            <div style={{
              width: '100%',
              marginTop: 16,
              background: 'rgba(255, 255, 255, 0.95)',
              padding: '12px 20px',
              borderRadius: 8,
              color: '#333',
              fontSize: 16,
              fontWeight: 600,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
            }} className="plot-photo-lightbox-caption">
              {new Date(fotoAmpliada.data_foto.length === 10 ? fotoAmpliada.data_foto + 'T12:00:00' : fotoAmpliada.data_foto).toLocaleDateString('pt-BR', { 
                day: '2-digit', 
                month: 'long', 
                year: 'numeric' 
              })}
              {fotoAmpliada.observacao && (
                <span style={{ marginLeft: 12, color: '#666', fontWeight: 400 }}>
                  • {fotoAmpliada.observacao}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
    )}
  </div>
);
}

export default AlbumFotos;
